import React from 'react';
import { css } from 'emotion';

import theme from 'config/theme';
import HelpMenu from 'components/app/topbar/HelpMenu';
import ProfileMenu from 'components/app/topbar/user/ProfileMenu';
import Navigation from 'components/app/topbar/navigation/Navigation';
import SubBar from 'components/app/SubBar';
import { EntityAttributesProvider, SavedFiltersProvider } from 'contexts';
import { MagicBellProvider } from '@magicbell/magicbell-react';
import useSWR from 'swr';
import { UserNotificationSettings } from 'waypoint-types';
import { getUserNotificationSettings } from 'waypoint-requests';
import NotificationBell from 'components/app/topbar/notifications/NotificationBell';
import { useGetUserNotificationPreferences } from 'waypoint-hooks';

const notificationTheme = {
    icon: { borderColor: '#ffffff', width: '24px' },
    unseenBadge: { backgroundColor: '#DF4759' },
    header: {
        backgroundColor: '#00ade2',
        textColor: '#ffffff',
        borderRadius: '16px',
        fontFamily: 'Helvetica',
    },
    footer: {
        backgroundColor: '#00ade2',
        textColor: '#ffffff',
        borderRadius: '16px',
        fontFamily: 'Helvetica',
    },
    notification: {
        default: {
            textColor: '#15091F',
            borderRadius: '12px',
            backgroundColor: '#00ade2',
            fontFamily: 'Helvetica',
        },
        unseen: {
            backgroundColor: '#00ade2',
            textColor: '#15091F',
            borderRadius: '12px',
            fontFamily: 'Helvetica',
        },
        unread: {
            backgroundColor: '#00ade2',
            textColor: '#15091F',
            borderRadius: '12px',
            fontFamily: 'Helvetica',
        },
    },
};
const notificationImages = {
    emptyInboxUrl:
        'https://gw.alipayobjects.com/zos/antfincdn/ZHrcdLPrvN/empty.svg',
};

const navigationContainer = css`
    display: flex;
    flex-grow: 1;
    justify-content: space-between;
    box-sizing: border-box;
    top: 0;
    width: 100vw;
    height: 50px;
    background-color: #424242;
`;

const quickFiltersContainer = (hideGlobalFilterBar: boolean) => {
    return css({
        display: 'flex',
        alignItems: 'center',
        height: '50px',
        paddingLeft: '10px',
        borderBottom: hideGlobalFilterBar
            ? 'none'
            : `1px solid ${theme.colors.grays.light}`,
    });
};

const container = css`
    display: flex;
    flex-direction: column;
`;

interface TopbarProps {
    hideGlobalFilterBar: boolean;
    hideDataLevelSelection: boolean;
}

const Topbar = ({
    hideGlobalFilterBar,
    hideDataLevelSelection,
}: TopbarProps) => {
    const { data: notificationSettings } = useSWR<UserNotificationSettings>(
        '/users/notification-settings',
        () => getUserNotificationSettings(),
        {
            revalidateOnMount: true,
            revalidateOnFocus: false,
        }
    );

    const {
        data: preferences,
        isLoading: isLoadingPreferences,
        mutate: preferencesMutate,
    } = useGetUserNotificationPreferences();

    return (
        <div className={container}>
            <div className={navigationContainer}>
                <Navigation />

                <div
                    style={{
                        display: 'flex',
                        order: 2,
                        alignItems: 'center',
                    }}
                >
                    {!!notificationSettings && (
                        <MagicBellProvider
                            apiKey={notificationSettings.apiKey}
                            userExternalId={notificationSettings.id.toString()}
                            userKey={notificationSettings.hmac}
                            theme={notificationTheme}
                            images={notificationImages}
                            locale="en"
                        >
                            <NotificationBell
                                preferences={preferences}
                                preferencesMutate={preferencesMutate}
                                isLoadingPreferences={isLoadingPreferences}
                            />
                        </MagicBellProvider>
                    )}

                    <div style={{ marginRight: '8px', marginLeft: '8px' }}>
                        <HelpMenu />
                    </div>

                    <ProfileMenu />
                </div>
            </div>

            <div className={quickFiltersContainer(hideGlobalFilterBar)}>
                <EntityAttributesProvider>
                    <SavedFiltersProvider>
                        <SubBar
                            hideGlobalFilters={hideGlobalFilterBar}
                            hideDataLevelSelection={hideDataLevelSelection}
                        />
                    </SavedFiltersProvider>
                </EntityAttributesProvider>
            </div>
        </div>
    );
};

export default Topbar;
